import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'preferredTheme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeState {
  theme: Theme;
  /** True while the theme is whatever the browser asks for, rather than something chosen here. */
  followingBrowser: boolean;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeState>({
  theme: 'light',
  followingBrowser: true,
  toggle: () => {},
});

/** What was chosen here before, or null while the browser's own preference is being followed. */
function stored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    // Private browsing with storage denied: the choice cannot be remembered, which is not a reason to fail.
    return null;
  }
}

function remember(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
  }
}

function browserPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/**
 * Puts the resolved theme on the document element: data-theme for this application's own tokens, and
 * data-bs-theme for Bootstrap's components, which read the attribute and nothing else.
 */
function apply(theme: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.bsTheme = theme;
}

/**
 * Applied before the first render, from main.tsx. The stylesheet already follows prefers-color-scheme, so a
 * dark-by-default browser is dark before any of this runs; this is for somebody whose browser says dark and
 * who chose light here, who would otherwise see the dark palette until React mounted.
 */
export function applyStoredTheme(): void {
  apply(stored() ?? (browserPrefersDark() ? 'dark' : 'light'));
}

/**
 * Light or dark, following the browser until somebody says otherwise. Two states rather than three, because
 * that is what a toggle is: the browser's preference is the starting point, and the first press of the button
 * is a decision that then sticks - which is what the listener below stops mattering after.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [chosen, setChosen] = useState<Theme | null>(stored);
  const [browserDark, setBrowserDark] = useState(browserPrefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const list = window.matchMedia(DARK_QUERY);
    const update = () => setBrowserDark(list.matches);

    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, []);

  const theme: Theme = chosen ?? (browserDark ? 'dark' : 'light');

  useEffect(() => apply(theme), [theme]);

  const toggle = useCallback(() => {
    setChosen((current) => {
      const next: Theme = (current ?? (browserPrefersDark() ? 'dark' : 'light')) === 'dark' ? 'light' : 'dark';
      remember(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, followingBrowser: chosen === null, toggle }),
    [theme, chosen, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  return useContext(ThemeContext);
}
